/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import '../../styles';

/**
 * @prop {String} image - Defines the profile picture. If left undefined a placeholder icon will be shown.
 * @prop {String} name - Defines the user name shown to the right side of the picture. If let undefined, only the image will be shown.
 * @prop {String} info - Defined the info text shown below the user name. Will only be shown if the name property is also defined.
 * @prop {Boolean} condensed - If set to `true`, condensed style is applied.
 * @prop {String} initials - 	Defines the initials shown inside the circle (up to 2 characters). If not defined, the first and last initials of the name will be displayed automatically.
 * @prop {Boolean} unauthenticated - If set to `true`, will display a placeholder icon with or without the surrounding circle, depending on the definition of the name property.
 */
export class SHUserIdentifier extends LitElement {
  static get properties() {
    return {
      image: { type: String, reflect: true },
      initials: { type: String, reflect: true },
      name: { type: String, reflect: true },
      info: { type: String, reflect: true },
      condensed: { type: Boolean, reflect: true },
      unauthenticated: { type: Boolean, reflect: true },
      /** @ignore */
      accessContext: {
        type: Boolean,
        reflect: true,
        attribute: 'access-context',
      },
    };
  }

  static get styles() {
    return [
      css`
        :host {
          display: flex;
          flex-direction: row;
          cursor: pointer;
          min-height: fit-content;
          min-height: -ms-fit-content;
          min-height: -moz-fit-content;
          outline: 0;
        }
        :host([access-context]) {
          width: fit-content;
          width: -ms-fit-content;
          width: -moz-fit-content;
        }
        .circle {
          width: 2rem;
          height: 2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: rgba(var(--ui-1), var(--opacity-6));
          border-radius: 50%;
          overflow: hidden;
          font: var(--title-1);
          transition: var(--transition-time) all ease-in-out;
        }
        .info-wrapper {
          flex-direction: column;
          display: flex;
          margin-left: 0.5rem;
          justify-content: center;
          flex: 1;
          overflow: hidden;
        }
        :host([access-context]) .info-wrapper {
          max-width: 10rem;
        }
        .name,
        .info {
          max-width: 10rem;
          line-height: 1rem;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          font-family: siemens sans, sans-serif;
          -webkit-font-smoothing: antialiased !important;
          text-rendering: optimizeLegibility !important;
          -moz-osx-font-smoothing: grayscale !important;
          height: 1rem;
          transition: var(--transition-time) all ease-in-out;
        }
        :host .name,
        :host .info,
        :host .circle,
        :host sh-icon[icon='user'] {
          color: var(--text-secondary);
        }
        :host(:hover) .name,
        :host(:hover) .info,
        :host(:hover) .circle,
        :host(:hover) sh-icon[icon='user'] {
          color: var(--text-primary);
        }
        .name {
          font-weight: bold;
          font: var(--title-1);
        }
        .info {
          font: var(--body-2);
        }
        /* unauthenticated */
        :host([unauthenticated]:not([name])) .circle {
          border-radius: initial;
          background-color: unset;
        }
        /* condensed access-bar */
        :host([access-context][condensed]) {
          margin: -0.25rem 0;
        }
        /* medium screens */
        @media only screen and (max-width: 64.0625rem) {
          :host([access-context]) .info-wrapper {
            display: none;
          }
        }
        /* focus */
        :host(.focus) {
          outline: var(--focus-outline);
        }
      `,
    ];
  }

  render() {
    return html`
      <div class="circle">
        ${this.unauthenticated
          ? html`
              ${this.name
                ? html` <sh-icon icon="user-avatar"></sh-icon> `
                : html` <sh-icon icon="user"></sh-icon> `}
            `
          : html`
              ${this.image
                ? html` <sh-icon icon="${this.image}"></sh-icon> `
                : html`
                    ${this.initials
                      ? this.formatInitials(this.initials)
                      : this.name
                      ? this.getInitials(this.name)
                      : ''}
                  `}
            `}
      </div>
      ${this.name
        ? html`
            <div class="info-wrapper">
              <div class="name">${this.name}</div>
              ${this.info && !this.unauthenticated
                ? html` <div class="info">${this.info}</div> `
                : ''}
            </div>
          `
        : ''}
    `;
  }

  attributeChangedCallback(name, oldval, newval) {
    super.attributeChangedCallback(name, oldval, newval);
    this.dispatchEvent(new Event(`${name}-changed`));
  }

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }
  }

  firstUpdated() {
    let self;
    self = this;
    this.addEventListener('focus', function () {
      this.onkeyup = function (e) {
        if (e.keyCode === 9) {
          self.classList.add('focus');
        }
      };
    });
    this.addEventListener('blur', function () {
      self.classList.remove('focus');
    });
  }

  getInitials(name) {
    const initials =
      name.split(/\s/).reduce((response, word) => (response += word.slice(0, 1)), '') || [];
    return this.formatInitials(initials);
  }

  formatInitials(initials) {
    const firstChar = initials.length > 0 ? initials.charAt(0) : '';
    const lastChar = initials.length > 1 ? initials.charAt(initials.length - 1) : '';
    return (firstChar + lastChar).toUpperCase();
  }
}

customElements.define('sh-user-identifier', SHUserIdentifier);
